import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  GridCellKind,
  type DataEditorRef,
  type GridCell,
  type GridColumn,
  type Item,
  type Rectangle,
} from '@glideapps/glide-data-grid'
import type { DropdownCellType } from '@glideapps/glide-data-grid-cells'
import { inferSchema, type ColumnSchema } from '../lib/inferSchema'
import { sortRows, type SortRule } from '../lib/sortRows'

type Row = Record<string, unknown>
// `__rowIdx` is the canonical absolute JSONL line index, stamped at fetch time.
// Tagging it here kills repeated `as number | undefined` casts at every read site.
type StampedRow = Row & { __rowIdx?: number }

// SEM-68: agent-declared action columns. Mirror of worker `ActionColumn`.
export type ActionColumn = {
  column: string
  type: 'checkbox' | 'select' | 'text'
  default?: unknown
  options?: string[]
}

const PAGE_SIZE = 1000
const PREFETCH_ROWS = 2000

export interface UseGridDataOpts {
  /** Total rows known from meta. The initial render uses this before page 0 arrives. */
  totalRows: number
  /** Fetcher for one page. Hook calls it with `(offset, limit)` and expects `{ rows, total }`. */
  fetchPage: (offset: number, limit: number) => Promise<{ rows: Row[]; total: number }>
  sortRules: SortRule[]
  colOrder: string[]
  colWidths: Record<string, number>
  /** Optional client-side full-text filter. Only applied once `fullyLoaded`. */
  search?: string
  /** When true, cells become editable in Glide (allowOverlay flips to true). HITL gate. */
  canEdit?: boolean
  /** SEM-68: when defined, ONLY action columns are editable; rendered with typed controls. */
  actions?: ActionColumn[] | null
}

export interface UseGridDataResult {
  gridRef: React.RefObject<DataEditorRef>
  schema: ColumnSchema[]
  columns: GridColumn[]
  /** Sorted+filtered rows when `fullyLoaded`; otherwise the partially-loaded `rawRows`. */
  rows: Row[]
  /** Row count to render: filtered length when fully loaded, else `totalRows`. */
  rowCount: number
  fullyLoaded: boolean
  pagesLoaded: number
  totalPages: number
  loadPct: number
  getCellContent: (item: Item) => GridCell
  onVisibleRegionChanged: (range: Rectangle) => void
  /** Look up a row by its visible index — works before and after `fullyLoaded`. */
  getRow: (index: number) => Row | undefined
  /** Like getRow, but returns the raw page-cache row without merging overlay/defaults.
   *  Use when you only need `__rowIdx`; cheaper and stable across edits. */
  getRawRow: (index: number) => StampedRow | undefined
  /** Apply an optimistic edit to the local overlay. rawRowIdx is the absolute JSONL line index. */
  applyEdit: (rawRowIdx: number, colKey: string, value: unknown) => void
}

export function useGridData(opts: UseGridDataOpts): UseGridDataResult {
  const { totalRows, fetchPage: fetchPageFn, sortRules, colOrder, colWidths, search, canEdit, actions } = opts
  const actionMap = useMemo(
    () => new Map((actions ?? []).map(a => [a.column, a])),
    [actions],
  )
  const hasActions = actionMap.size > 0

  const gridRef = useRef<DataEditorRef>(null)
  const [schema, setSchema] = useState<ColumnSchema[]>([])
  const [pagesLoaded, setPagesLoaded] = useState(0)
  const [fullyLoaded, setFullyLoaded] = useState(false)
  const [rawRows, setRawRows] = useState<Row[]>([])
  // Sparse local edit overlay keyed by absolute row index (__rowIdx). Survives sort/filter.
  const [editOverlay, setEditOverlay] = useState<Map<number, Record<string, unknown>>>(new Map())

  const pageCache = useRef<Map<number, Row[]>>(new Map())
  const pendingPages = useRef<Set<number>>(new Set())
  const allRowsRef = useRef<Row[]>([])
  const schemaRef = useRef<ColumnSchema[]>([])

  const schemaMap = useMemo(() => new Map(schema.map(s => [s.key, s])), [schema])

  const sortedRows = useMemo(
    () => (fullyLoaded ? sortRows(rawRows, sortRules) : rawRows),
    [rawRows, sortRules, fullyLoaded],
  )

  const filteredRows = useMemo(() => {
    if (!fullyLoaded || !search?.trim()) return sortedRows
    const needle = search.toLowerCase()
    return sortedRows.filter(r =>
      Object.values(r).some(v => String(v ?? '').toLowerCase().includes(needle)),
    )
  }, [sortedRows, search, fullyLoaded])

  const baseCols = useMemo(() => {
    // SEM-68: pin action columns to the right of the data columns. Synthesize entries
    // for action columns absent from the JSONL schema so they always render.
    const dataCols = schema
      .filter(s => !actionMap.has(s.key))
      .map(s => ({ id: s.key, title: s.key, defaultWidth: 160 }))
    if (!hasActions) return dataCols
    const actionCols = (actions ?? []).map(a => ({ id: a.column, title: a.column, defaultWidth: 160 }))
    return [...dataCols, ...actionCols]
  }, [schema, actions, actionMap, hasActions])

  const columns = useMemo<GridColumn[]>(() => {
    // SEM-68: when actions are defined, action columns stay pinned to the right; user
    // colOrder is honoured only for data columns.
    let ordered: typeof baseCols
    if (hasActions) {
      const dataCols = baseCols.filter(c => !actionMap.has(c.id))
      const actionCols = baseCols.filter(c => actionMap.has(c.id))
      const orderedData = colOrder.length > 0
        ? [
            ...colOrder.flatMap(colId => dataCols.find(c => c.id === colId) ?? []),
            ...dataCols.filter(c => !colOrder.includes(c.id)),
          ]
        : dataCols
      ordered = [...orderedData, ...actionCols]
    } else {
      ordered = colOrder.length > 0
        ? [
            ...colOrder.flatMap(colId => baseCols.find(c => c.id === colId) ?? []),
            ...baseCols.filter(c => !colOrder.includes(c.id)),
          ]
        : baseCols
    }
    return ordered.map((c, i) => {
      const rule = fullyLoaded ? sortRules.find(r => r.col === c.id) : undefined
      const arrow = rule ? (rule.dir === 'asc' ? ' ↑' : ' ↓') : ''
      const multiIdx =
        fullyLoaded && sortRules.length > 1 ? sortRules.findIndex(r => r.col === c.id) : -1
      const label =
        multiIdx >= 0 ? `${c.title}${arrow}${multiIdx + 1}` : `${c.title}${arrow}`
      const isFirstAction =
        hasActions && actionMap.has(c.id) && (i === 0 || !actionMap.has(ordered[i - 1].id))
      // Subtle separator marking the start of the action region.
      const themeOverride = isFirstAction ? { borderColor: '#46a361' } : undefined
      return { title: label, id: c.id, width: colWidths[c.id] ?? c.defaultWidth, themeOverride }
    })
  }, [baseCols, colOrder, colWidths, sortRules, fullyLoaded, hasActions, actionMap])

  const loadPage = useCallback((pageIndex: number) => {
    if (pageCache.current.has(pageIndex)) return
    if (pendingPages.current.has(pageIndex)) return
    pendingPages.current.add(pageIndex)

    const offset = pageIndex * PAGE_SIZE
    fetchPageFn(offset, PAGE_SIZE)
      .then(({ rows, total }) => {
        // Stamp absolute JSONL line index as canonical row identity for edits.
        // Survives sort/filter — used to map display coords back to backend row.
        for (let i = 0; i < rows.length; i++) {
          rows[i] = { ...rows[i], __rowIdx: offset + i }
        }
        pageCache.current.set(pageIndex, rows)
        pendingPages.current.delete(pageIndex)

        if (pageIndex === 0 && schemaRef.current.length === 0) {
          const s = inferSchema(rows)
          schemaRef.current = s
          setSchema(s)
        }

        for (let i = 0; i < rows.length; i++) {
          allRowsRef.current[offset + i] = rows[i]
        }

        const totalPages = Math.ceil(total / PAGE_SIZE)
        const loaded = pageCache.current.size
        setPagesLoaded(loaded)

        if (loaded >= totalPages) {
          setRawRows(allRowsRef.current.filter(Boolean))
          setFullyLoaded(true)
        }

        if (pageIndex > 0 && schemaRef.current.length > 0) {
          const colCount = schemaRef.current.length
          const cells = rows.flatMap((_, rowOffset) =>
            Array.from({ length: colCount }, (_, col) => ({
              cell: [col, offset + rowOffset] as [number, number],
            })),
          )
          gridRef.current?.updateCells(cells)
        }
      })
      .catch(() => {
        pendingPages.current.delete(pageIndex)
      })
  }, [fetchPageFn])

  // Kick off page 0 once we know the total. Schema guard prevents re-fetch.
  useEffect(() => {
    if (totalRows > 0 && schemaRef.current.length === 0) {
      loadPage(0)
    }
  }, [totalRows, loadPage])

  const onVisibleRegionChanged = useCallback((range: Rectangle) => {
    const firstPage = Math.floor(range.y / PAGE_SIZE)
    const lastPage = Math.floor((range.y + range.height + PREFETCH_ROWS) / PAGE_SIZE)
    for (let p = firstPage; p <= lastPage; p++) loadPage(p)
  }, [loadPage])

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const colKey = columns[col]?.id ?? ''

      let value: unknown
      let displayedRow: Row | undefined
      if (fullyLoaded) {
        displayedRow = filteredRows[row]
        value = displayedRow?.[colKey]
      } else {
        const pageIndex = Math.floor(row / PAGE_SIZE)
        const page = pageCache.current.get(pageIndex)
        if (!page) {
          loadPage(pageIndex)
          return { kind: GridCellKind.Loading, allowOverlay: false }
        }
        displayedRow = page[row % PAGE_SIZE]
        value = displayedRow?.[colKey]
      }

      // Apply local edit overlay if present — keyed by absolute row index (__rowIdx)
      const rawIdx = (displayedRow as StampedRow | undefined)?.__rowIdx
      if (rawIdx !== undefined) {
        const overrides = editOverlay.get(rawIdx)
        if (overrides && colKey in overrides) value = overrides[colKey]
      }

      // SEM-68: when actions are declared, only those columns are editable.
      // Synthesize the default for action columns missing from the JSONL.
      const action = actionMap.get(colKey)
      if (action !== undefined && (value === undefined || value === null) && action.default !== undefined) {
        value = action.default
      }
      const allowOverlay = (canEdit ?? false) && (hasActions ? !!action : true)

      if (action) {
        if (action.type === 'checkbox') {
          // Glide's Boolean cell toggles on click; allowOverlay must be `false`,
          // and `readonly` controls whether clicks toggle the value.
          return { kind: GridCellKind.Boolean, data: !!value, allowOverlay: false, readonly: !allowOverlay }
        }
        if (action.type === 'select') {
          const strValue = typeof value === 'string' ? value : ''
          return {
            kind: GridCellKind.Custom,
            allowOverlay,
            copyData: strValue,
            data: {
              kind: 'dropdown-cell',
              allowedValues: action.options ?? [],
              value: strValue,
            },
          } as DropdownCellType
        }
        // type === 'text' falls through to Text rendering below.
      }

      const kind = schemaMap.get(colKey)?.kind
      if ((kind === 'number' || kind === 'currency') && typeof value === 'number') {
        const displayData =
          kind === 'currency'
            ? value.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
            : value.toLocaleString()
        return { kind: GridCellKind.Number, data: value, displayData, allowOverlay }
      }

      const display =
        typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '')
      return {
        kind: GridCellKind.Text,
        data: display,
        displayData: display,
        allowOverlay,
      }
    },
    [columns, schemaMap, fullyLoaded, filteredRows, loadPage, editOverlay, canEdit, actionMap, hasActions],
  )

  const getRawRow = useCallback((index: number): StampedRow | undefined => {
    return fullyLoaded
      ? filteredRows[index]
      : pageCache.current.get(Math.floor(index / PAGE_SIZE))?.[index % PAGE_SIZE]
  }, [fullyLoaded, filteredRows])

  const getRow = useCallback((index: number): StampedRow | undefined => {
    const base = getRawRow(index)
    if (!base) return base
    // Keeps consumers (e.g. RowInspector) consistent with what `getCellContent` renders:
    // synthesize action-column defaults for missing keys, then local edit overlay on top.
    const rawIdx = base.__rowIdx
    const overrides = rawIdx !== undefined ? editOverlay.get(rawIdx) : undefined
    if (!hasActions && !overrides) return base
    const defaults: Row = {}
    if (hasActions) {
      for (const action of actionMap.values()) {
        const cur = base[action.column]
        if ((cur === undefined || cur === null) && action.default !== undefined) {
          defaults[action.column] = action.default
        }
      }
    }
    return { ...base, ...defaults, ...overrides }
  }, [getRawRow, editOverlay, hasActions, actionMap])

  const applyEdit = useCallback((rawRowIdx: number, colKey: string, value: unknown) => {
    setEditOverlay(prev => {
      const next = new Map(prev)
      const row = next.get(rawRowIdx) ?? {}
      next.set(rawRowIdx, { ...row, [colKey]: value })
      return next
    })
    // Repaint the affected cells so the optimistic value shows immediately
    if (gridRef.current) {
      // Find display row(s) by matching __rowIdx — works in both paged and fully-loaded modes
      const colIdx = columns.findIndex(c => c.id === colKey)
      if (colIdx < 0) return
      if (fullyLoaded) {
        for (let r = 0; r < filteredRows.length; r++) {
          if ((filteredRows[r] as StampedRow | undefined)?.__rowIdx === rawRowIdx) {
            gridRef.current.updateCells([{ cell: [colIdx, r] }])
            break
          }
        }
      } else {
        gridRef.current.updateCells([{ cell: [colIdx, rawRowIdx] }])
      }
    }
  }, [columns, fullyLoaded, filteredRows])

  const rowCount = fullyLoaded ? filteredRows.length : totalRows
  const totalPages = Math.ceil(totalRows / PAGE_SIZE)
  const loadPct = totalPages > 0 ? Math.round((pagesLoaded / totalPages) * 100) : 0

  return {
    gridRef,
    schema,
    columns,
    rows: filteredRows,
    rowCount,
    fullyLoaded,
    pagesLoaded,
    totalPages,
    loadPct,
    getCellContent,
    onVisibleRegionChanged,
    getRow,
    getRawRow,
    applyEdit,
  }
}

export const GRID_PAGE_SIZE = PAGE_SIZE
